import { UserType } from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { NavLink, useLocation } from "react-router-dom";
import inrevLogoIcon from "../../../assets/logos/inrev-logo-icon-white.svg";
import inrevLogo from "../../../assets/logos/inrev-logo-white.svg";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { Button } from "../Button";
import { MainNavItem } from "./components/MainNavItem";

type MainNavProps = {
	label?: string;
	smallLabel?: string;
	items: {
		dashboard: boolean;
		requests: boolean;
		bonds: boolean;
		accounts: boolean;
		submissions?: boolean;
		marketAnalytics?: boolean;
	};
};

export const MainNav = ({ label, smallLabel, items }: MainNavProps) => {
	const { user } = useAuthenticatedUser();

	const { pathname } = useLocation();
	const nestedSubResource = pathname.match(/^\/accounts\/[^/]+\/(requests|bonds)\//)?.[1];

	const navOrder = ["submissions", "accounts", "requests", "bonds", "marketAnalytics"] as const;

	const navConfig = {
		accounts: {
			to: "/accounts",
			label: "Accounts",
			icon: "building-office" as const,
		},
		submissions: {
			to: "/submissions",
			label: "Submissions",
			icon: "archive-box-arrow-down" as const,
		},
		requests: {
			to: "/requests",
			label: "Bond Requests",
			icon: "clipboard" as const,
		},
		bonds: {
			to: "/bonds",
			label: "Bonds",
			icon: "shield-check" as const,
		},
		marketAnalytics: {
			to: "/market-analytics",
			label: "Market Analytics",
			icon: "presentation-chart-line" as const,
		},
	};

	return (
		<div className="flex flex-col w-full h-screen bg-surface-primary py-[32px] space-y-[33px] shrink-0">
			<div className="flex justify-center md:justify-normal w-full px-0 md:px-[32px] shrink-0">
				<img src={inrevLogo} className="h-[25px] hidden md:block" />
				<img src={inrevLogoIcon} className="h-[25px] block md:hidden" />
			</div>
			<div className="flex flex-col space-y-2 w-full px-0 md:px-[22px] shrink-0">
				{label && (
					<div className="text-inverse font-medium py-[4px] mb-[10px] w-full flex items-center justify-center text-[13px] bg-brand-primary-lighter">
						{smallLabel && <span className="inline md:hidden">{smallLabel}</span>}
						<span className={cn(smallLabel && "hidden md:inline")}>{label}</span>
					</div>
				)}
				{items.dashboard && (
					<NavLink to="/" tabIndex={-1}>
						{({ isActive }) => (
							<MainNavItem
								onClick={() => {}}
								active={isActive}
								label="Dashboard"
								icon="squares-2x2"
							/>
						)}
					</NavLink>
				)}
				{navOrder.map(
					(key) =>
						items[key] && (
							<NavLink key={key} to={navConfig[key].to} tabIndex={-1}>
								{({ isActive }) => {
									const active = nestedSubResource ? key === nestedSubResource : isActive;
									return (
										<MainNavItem
											onClick={() => {}}
											active={active}
											label={navConfig[key].label}
											icon={navConfig[key].icon}
										/>
									);
								}}
							</NavLink>
						),
				)}
			</div>
			{user.type === UserType.admin && (
				<div title="Import Bond" className="w-full flex-1 flex items-end justify-center">
					<NavLink to="/import/bonds">
						<Button color="inverse" rounded className="text-[12px] h-[24px] px-3" thinFont>
							<span className="hidden md:inline">Import Bond</span>
							<Icon className="inline md:hidden" size="micro" type="arrow-up-tray" />
						</Button>
					</NavLink>
				</div>
			)}
		</div>
	);
};
